import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { FinishPlayAttemptInput, GoalArenaQuestionResult, GoalArenaShotOutcome } from '../../../shared/results';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import { FootballAudio } from './FootballAudio';
import type { GoalArenaConfig } from './domain/goalLevels';
import { applyGoalAnswer, initialGoalRuleState, type GoalRuleState } from './domain/goalRules';
import { buildAimedShotProfile, classifyAim, goalAimRules, initialKeyboardAim, intersectGoalPlane, moveKeyboardAim, type AimPoint, type AimZone } from './domain/aimModel';
import { selectRandomMissProfile, type KeeperAction } from './domain/shotProfiles';
import { goalArenaPresentation as P } from './three/goalArenaPresentation';

type ArenaController = import('./three/GoalArenaController').GoalArenaController;
type Props = { config: GoalArenaConfig; label?: string; restartEnabled?: boolean; onFinished?: (result: FinishPlayAttemptInput) => void };
/** question: kart bekleniyor · aim: doğru cevap, öğrenci nişan alıyor · own-shot: öğrencinin şutu havada · reveal: yanlış, doğru cevap gösteriliyor · auto-shot: yanlışın otomatik şutu */
type Phase = 'question' | 'aim' | 'own-shot' | 'reveal' | 'auto-shot' | 'finished';
type ScreenPoint = { x: number; y: number };
type Drag = { end: ScreenPoint; zone: AimZone };
type PendingAnswer = {
  choiceIndex: number; correctChoiceIndex: number; durationMs: number;
  calculation: ReturnType<typeof applyGoalAnswer>; selectedAnswer: string; correctAnswer: string;
};

/** Yanlışta doğru cevabın otomatik şuttan önce görünme süresi ve şut sonrası okuma süreleri (ms). */
const REVEAL_MS = 1500;
const AFTER_GOAL_MS = 900;
const AFTER_AUTO_SHOT_MS = 1900;

function answerLabel(text: string, hasMedia: boolean) { return text.trim() || (hasMedia ? 'Görsel seçenek' : 'Boş seçenek'); }
function missLabel(outcome: GoalArenaShotOutcome) {
  if (outcome === 'save') return 'Kaleci çıkardı!';
  if (outcome === 'post') return 'Direkten döndü!';
  if (outcome === 'crossbar') return 'Üst direkten döndü!';
  if (outcome === 'wide') return 'Top yandan dışarı gitti!';
  return 'Top üstten dışarı gitti!';
}
const zoneTone: Record<AimZone, 'inside' | 'outside' | 'short'> = { inside: 'inside', post: 'outside', crossbar: 'outside', wide: 'outside', high: 'outside', short: 'short' };

export function GoalArenaGame({ config, label = 'Gol Arenası 3B oyun alanı', restartEnabled = true, onFinished }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<ArenaController | null>(null);
  const audioRef = useRef(new FootballAudio());
  const onFinishedRef = useRef(onFinished);
  const questionStartedAt = useRef(performance.now());
  const runStartedAt = useRef(performance.now());
  const resultSent = useRef(false);
  const lastMissId = useRef<string | undefined>(undefined);
  const pending = useRef<PendingAnswer | null>(null);
  const attemptsRef = useRef(0);
  const rulesRef = useRef<GoalRuleState>(initialGoalRuleState());
  const resultsRef = useRef<GoalArenaQuestionResult[]>([]);
  const questionIndexRef = useRef(0);
  const disposed = useRef(false);
  const timers = useRef(new Set<number>());
  const [questionIndex, setQuestionIndex] = useState(0);
  const [rules, setRules] = useState<GoalRuleState>(initialGoalRuleState());
  const [questionResults, setQuestionResults] = useState<GoalArenaQuestionResult[]>([]);
  const [feedback, setFeedback] = useState<string | null>('Stadyum hazır. İlk soru seni bekliyor!');
  const [tone, setTone] = useState<'neutral' | 'shot' | 'correct' | 'wrong'>('neutral');
  const [muted, setMuted] = useState(() => audioRef.current.isMuted());
  const [phase, setPhase] = useState<Phase>('question');
  const [ready, setReady] = useState(false);
  const [answer, setAnswer] = useState<{ selected: number; correct: number } | null>(null);
  const [misses, setMisses] = useState(0);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [keyboardAim, setKeyboardAim] = useState<AimPoint | null>(null);
  const [geometry, setGeometry] = useState<{ ball: ScreenPoint & { radius: number }; frame: ScreenPoint[] } | null>(null);
  const [ballState, setBallState] = useState('ready');
  const [keeperState, setKeeperState] = useState<KeeperAction | 'idle'>('idle');

  const wait = (ms: number) => new Promise<void>((resolve) => {
    const id = window.setTimeout(() => { timers.current.delete(id); resolve(); }, ms); timers.current.add(id);
  });

  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);

  useLayoutEffect(() => {
    disposed.current = false;
    let cancelled = false;
    void import('./three/GoalArenaController').then(({ GoalArenaController }) => {
      if (cancelled || !containerRef.current) return;
      gameRef.current = new GoalArenaController(containerRef.current); setReady(true);
    });
    const pendingTimers = timers.current;
    return () => {
      cancelled = true; disposed.current = true; pendingTimers.forEach((id) => window.clearTimeout(id)); pendingTimers.clear();
      gameRef.current?.dispose(); gameRef.current = null; audioRef.current.dispose();
    };
  }, []);

  const measure = useCallback(() => {
    const game = gameRef.current; if (!game) return;
    setGeometry({ ball: game.ballScreen(), frame: game.goalFrameScreen() });
  }, []);
  useEffect(() => {
    if (phase !== 'aim' || !ready) return;
    measure();
    const observer = new ResizeObserver(measure); if (overlayRef.current) observer.observe(overlayRef.current);
    return () => observer.disconnect();
  }, [measure, phase, ready]);

  const current = config.questions[questionIndex];

  const finish = (nextRules: GoalRuleState, nextResults: GoalArenaQuestionResult[]) => {
    setPhase('finished'); setBallState('ready'); setKeeperState('idle');
    setFeedback(nextRules.goals === config.questions.length ? 'Kusursuz maç!' : 'Maç tamamlandı!'); setTone('correct');
    audioRef.current.finish(nextRules.goals === config.questions.length);
    if (resultSent.current) return; resultSent.current = true;
    onFinishedRef.current?.({
      score: nextRules.score, lives: 0, correct: nextRules.goals, wrong: nextRules.wrong, attempts: nextRules.answered, completed: true,
      details: { templateKey: 'goal-arena', maxStreak: nextRules.maxStreak, goals: nextRules.goals, questions: nextResults },
    });
  };

  /** Sorunun kaydını yazar ve sonraki soruya/sonuca geçer. Puan kuralı cevap anında hesaplanan değerdir. */
  const commit = (shotOutcome: GoalArenaShotOutcome, shotAttempts: number) => {
    const answered = pending.current; const question = config.questions[questionIndexRef.current];
    if (!answered || !question) return;
    const record: GoalArenaQuestionResult = {
      level: question.id, durationMs: answered.durationMs, selectedChoiceIndex: answered.choiceIndex, correctChoiceIndex: answered.correctChoiceIndex,
      correct: answered.choiceIndex === answered.correctChoiceIndex, shotOutcome, shotAttempts: Math.min(goalAimRules.maxRecordedAttempts, shotAttempts),
      baseScore: answered.calculation.breakdown.baseScore, streakBonus: answered.calculation.breakdown.streakBonus, scoreDelta: answered.calculation.scoreDelta,
      questionPrompt: question.prompt, selectedAnswer: answered.selectedAnswer, correctAnswer: answered.correctAnswer,
    };
    const nextResults = [...resultsRef.current, record]; resultsRef.current = nextResults; setQuestionResults(nextResults);
    rulesRef.current = answered.calculation.state; setRules(answered.calculation.state);
    pending.current = null; setAnswer(null); setMisses(0); setDrag(null); setKeyboardAim(null); attemptsRef.current = 0;
    if (questionIndexRef.current + 1 >= config.questions.length) { finish(answered.calculation.state, nextResults); return; }
    questionIndexRef.current += 1; setQuestionIndex(questionIndexRef.current);
    questionStartedAt.current = performance.now(); setFeedback(null); setTone('neutral'); setPhase('question');
  };

  const choose = async (choiceIndex: number) => {
    if (!ready || phase !== 'question' || !current) return;
    audioRef.current.unlock();
    const choice = current.choices[choiceIndex]; const correctChoiceIndex = current.choices.findIndex((candidate) => candidate.correct);
    const correct = Boolean(choice?.correct);
    pending.current = {
      choiceIndex, correctChoiceIndex, durationMs: Math.max(0, Math.round(performance.now() - questionStartedAt.current)),
      calculation: applyGoalAnswer(rulesRef.current, correct),
      selectedAnswer: answerLabel(choice?.label ?? '', Boolean(choice?.mediaUrl)),
      correctAnswer: answerLabel(current.choices[correctChoiceIndex]?.label ?? '', Boolean(current.choices[correctChoiceIndex]?.mediaUrl)),
    };
    setAnswer({ selected: choiceIndex, correct: correctChoiceIndex });
    if (correct) {
      attemptsRef.current = 0; setMisses(0); setKeyboardAim(null);
      setPhase('aim'); setTone('correct');
      setFeedback('Doğru cevap! Şimdi topu tutup kaleye doğru sürükle ve şutunu kendin çek.');
      return;
    }
    setPhase('reveal'); setTone('wrong'); setFeedback(`Yanlış cevap. Doğru cevap: ${pending.current.correctAnswer}`);
    await wait(REVEAL_MS); if (disposed.current) return;
    setPhase('auto-shot'); setTone('shot'); setFeedback('Şut otomatik çekiliyor…'); audioRef.current.kick();
    const profile = selectRandomMissProfile(Math.random(), lastMissId.current); lastMissId.current = profile.id;
    setBallState('flight'); setKeeperState(profile.keeperAction);
    await gameRef.current?.shoot(profile, (outcome) => {
      setBallState(outcome); audioRef.current.impact(outcome, 0); setTone('wrong');
      setFeedback(`${missLabel(outcome)} Doğru cevabı verseydin şutu kendin çekecektin!`);
    });
    if (disposed.current) return;
    setBallState('ready'); setKeeperState('idle');
    await wait(AFTER_AUTO_SHOT_MS); if (disposed.current) return;
    commit(profile.outcome, 1);
  };

  const shootAt = async (point: AimPoint | null, zone: AimZone) => {
    if (phase !== 'aim' || !pending.current) return;
    setDrag(null);
    if (!point || zone === 'short') { setTone('neutral'); setFeedback('Topu kaleye doğru sürükle; çizgi kalenin içinde yeşil olur.'); return; }
    attemptsRef.current += 1; const attempts = attemptsRef.current;
    const profile = buildAimedShotProfile(point, zone);
    setPhase('own-shot'); setTone('shot'); setFeedback('ŞUT!'); audioRef.current.kick();
    setBallState('flight'); setKeeperState(profile.keeperAction);
    const streak = pending.current.calculation.state.streak; const { scoreDelta, breakdown } = pending.current.calculation;
    await gameRef.current?.shoot(profile, (outcome) => {
      setBallState(outcome); audioRef.current.impact(outcome, streak);
      if (outcome === 'goal') {
        setTone('correct');
        setFeedback(`GOL! +${scoreDelta}${breakdown.streakBonus ? ` · Seri bonusu +${breakdown.streakBonus}` : ''}${attempts > 1 ? ` · ${attempts}. şutta` : ''}`);
      } else {
        setTone('wrong'); setFeedback(`${missLabel(outcome)} Kaleyi hedefle ve tekrar dene.`);
      }
    });
    if (disposed.current) return;
    setBallState('ready'); setKeeperState('idle');
    if (profile.outcome === 'goal') { await wait(AFTER_GOAL_MS); if (!disposed.current) commit('goal', attempts); return; }
    setMisses((count) => count + 1); setPhase('aim');
  };

  const localPoint = (event: ReactPointerEvent<HTMLDivElement>): ScreenPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const aimAt = (end: ScreenPoint): { point: AimPoint | null; zone: AimZone } => {
    const game = gameRef.current; const ball = geometry?.ball;
    if (!game || !ball || Math.hypot(end.x - ball.x, end.y - ball.y) < goalAimRules.minDragPx) return { point: null, zone: 'short' };
    const ray = game.screenRay(end.x, end.y); const point = intersectGoalPlane(ray.origin, ray.direction);
    return { point, zone: classifyAim(point) };
  };
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (phase !== 'aim' || !geometry) return;
    const point = localPoint(event);
    if (Math.hypot(point.x - geometry.ball.x, point.y - geometry.ball.y) > Math.max(goalAimRules.grabRadiusPx, geometry.ball.radius * 3)) {
      setTone('neutral'); setFeedback('Topu tut ve kaleye doğru sürükle.'); return;
    }
    event.currentTarget.setPointerCapture(event.pointerId); setKeyboardAim(null);
    setDrag({ end: point, zone: 'short' });
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return; const end = localPoint(event); setDrag({ end, zone: aimAt(end).zone });
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return; const { point, zone } = aimAt(localPoint(event)); void shootAt(point, zone);
  };

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const name = event.key.toLowerCase();
      if (name === 'm') { setMuted(audioRef.current.toggleMuted()); return; }
      if (phase !== 'aim') return;
      const moves: Record<string, [number, number]> = { arrowleft: [-1, 0], arrowright: [1, 0], arrowup: [0, 1], arrowdown: [0, -1] };
      if (moves[name]) { event.preventDefault(); setKeyboardAim((aim) => moveKeyboardAim(aim ?? initialKeyboardAim, ...moves[name])); return; }
      if (name === 'enter' || name === ' ') {
        event.preventDefault(); const aim = keyboardAim ?? initialKeyboardAim; void shootAt(aim, classifyAim(aim));
      }
    };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  });

  const keyboardEnd = keyboardAim && gameRef.current ? gameRef.current.projectToScreen({ x: keyboardAim.x, y: keyboardAim.y, z: P.goal.z }) : null;
  const aimLine = drag ? { end: drag.end, zone: drag.zone } : keyboardEnd && keyboardAim ? { end: keyboardEnd, zone: classifyAim(keyboardAim) } : null;

  useEffect(() => {
    window.render_game_to_text = () => JSON.stringify({
      template: 'goal-arena', phase: ready ? phase : 'loading',
      question: Math.min(questionIndex + 1, config.questions.length), totalQuestions: config.questions.length,
      goals: rules.goals, score: rules.score, streak: rules.streak, wrong: rules.wrong,
      shotAttempts: attemptsRef.current, missedShots: misses, aimZone: aimLine?.zone ?? null,
      ball: gameRef.current?.getDebugState().ball ?? ballState,
      goalkeeper: gameRef.current?.getDebugState().goalkeeper ?? keeperState,
    });
    window.advanceTime = (ms: number) => { gameRef.current?.advanceTime(ms); };
    return () => { window.render_game_to_text = () => '{}'; window.advanceTime = () => undefined; };
  }, [aimLine?.zone, ballState, config.questions.length, keeperState, misses, phase, questionIndex, ready, rules]);

  const restart = () => {
    questionIndexRef.current = 0; rulesRef.current = initialGoalRuleState(); resultsRef.current = []; pending.current = null; attemptsRef.current = 0;
    setQuestionIndex(0); setRules(initialGoalRuleState()); setQuestionResults([]); setFeedback('Yeni maç başlıyor!'); setTone('neutral');
    setPhase('question'); setAnswer(null); setMisses(0); setDrag(null); setKeyboardAim(null); setBallState('ready'); setKeeperState('idle');
    resultSent.current = false; lastMissId.current = undefined;
    questionStartedAt.current = performance.now(); runStartedAt.current = performance.now();
  };

  const finished = phase === 'finished';
  const shooting = phase === 'aim' || phase === 'own-shot';
  const mistakes = questionResults.filter((result) => !result.correct);
  return (
    <FullscreenGameShell gameRef={gameRef} className="goal-arena-shell" toolbar={<>
      <strong className="toolbar-level">SORU {Math.min(questionIndex + 1, config.questions.length)} / {config.questions.length}</strong>
      <span className="goal-score-chip">⚽ Gol: {rules.goals}</span>
      <span className="goal-streak-chip">🔥 Seri: {rules.streak}</span>
      <span className="toolbar-score">Puan: {rules.score}</span>
      <button className="goal-sound-button" type="button" onClick={() => setMuted(audioRef.current.toggleMuted())} aria-pressed={muted} aria-label={muted ? 'Sesi aç' : 'Sesi kapat'} title="Ses (M)">
        <span aria-hidden="true">{muted ? '🔇' : '🔊'}</span><b>{muted ? 'Ses kapalı' : 'Ses açık'}</b>
      </button>
    </>}>
      <div className="goal-stage" role="application" aria-label={label}>
        <div className="goal-three-canvas" ref={containerRef} />
        <div className="goal-vignette" aria-hidden="true" />
        {!ready && <div className="goal-loading" role="status">3B stadyum hazırlanıyor…</div>}
        {!finished && current && <div className={`goal-question-layer${shooting ? ' aiming' : ''}`}>
          <section className="goal-scoreboard-question" aria-label="Soru">
            <small>ŞAMPİYONLUK GECESİ · {current.id}. ŞUT</small>
            {current.promptMediaUrl && <img src={current.promptMediaUrl} alt="Soru görseli" />}
            {current.prompt && <h2>{current.prompt}</h2>}
          </section>
          {feedback && <p className={`goal-feedback ${tone}`} role="status">{feedback}</p>}
          <div className={`goal-choice-grid choices-${current.choices.length}`} aria-hidden={shooting}>
            {current.choices.map((choice, index) => <button type="button" key={choice.id} disabled={!ready || phase !== 'question'} onClick={() => void choose(index)}
              className={answer && index === answer.correct ? 'is-correct' : answer && index === answer.selected ? 'is-wrong' : undefined}>
              <span className="goal-choice-number">{answer && index === answer.correct ? '✓' : answer && index === answer.selected ? '✕' : index + 1}</span>
              {choice.mediaUrl && <img src={choice.mediaUrl} alt="" />}
              {choice.label && <strong>{choice.label}</strong>}
            </button>)}
          </div>
        </div>}
        {!finished && <div ref={overlayRef} className={`goal-aim-layer${phase === 'aim' ? ' active' : ''}${drag ? ' dragging' : ''}`}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => setDrag(null)}
          aria-hidden={phase !== 'aim'}>
          {phase === 'aim' && geometry && <svg className="goal-aim-svg" aria-hidden="true">
            {misses >= goalAimRules.hintAfterMisses && <polygon className="goal-aim-frame" points={geometry.frame.map((point) => `${point.x},${point.y}`).join(' ')} />}
            {!aimLine && <>
              <circle className="goal-aim-ball-ring" cx={geometry.ball.x} cy={geometry.ball.y} r={geometry.ball.radius * 1.9} />
              <text className="goal-aim-hand" x={geometry.ball.x + geometry.ball.radius * 1.4} y={geometry.ball.y + geometry.ball.radius * 2.2}>👆</text>
            </>}
            {aimLine && <g className={`goal-aim-line ${zoneTone[aimLine.zone]}`}>
              <line x1={geometry.ball.x} y1={geometry.ball.y} x2={aimLine.end.x} y2={aimLine.end.y} />
              <circle cx={aimLine.end.x} cy={aimLine.end.y} r={11} />
            </g>}
          </svg>}
          {phase === 'aim' && <p className="goal-aim-help">Sürükle ve bırak · Klavye: ok tuşları ile nişan, Enter ile şut</p>}
        </div>}
        {finished && <section className="goal-result-overlay" aria-label="Oyun sonucu">
          <p className="eyebrow">Maç sonu</p>
          <h2>{rules.goals === config.questions.length ? 'KUSURSUZ MAÇ!' : 'GOL ARENASI TAMAMLANDI!'}</h2>
          <p>{rules.goals}/{config.questions.length} gol · {rules.score} puan · En uzun seri {rules.maxStreak}</p>
          <p>Toplam süre {Math.max(1, Math.round((performance.now() - runStartedAt.current) / 1000))} saniye</p>
          {mistakes.length > 0 && <div className="goal-mistake-review"><strong>Tekrar göz atalım</strong><ul>
            {mistakes.map((result) => <li key={result.level}>S{result.level}: {result.questionPrompt || 'Görsel soru'} — Doğru: {result.correctAnswer}</li>)}
          </ul></div>}
          {restartEnabled && <button className="primary-button" type="button" onClick={restart}>Yeniden oyna</button>}
        </section>}
      </div>
    </FullscreenGameShell>
  );
}
